/**
 * Minting new entity ids: time-ordered UUID v7 (RFC 9562 §5.7).
 *
 * The API and the Postgres ETL both call {@link newEntityId}, so an id minted by either looks
 * and sorts the same way. v7 rather than v4 because the leading 48 bits are the creation time in
 * milliseconds: ids sort by when the row was made, which keeps a b-tree primary key appending at
 * its right-hand edge instead of scattering inserts across the index, and gives `ORDER BY id` the
 * same meaning `_id` order had on MongoDB.
 *
 * Isomorphic on purpose — only `globalThis.crypto.getRandomValues`, which Node 20+ and every
 * browser provide — and no dependency, so the shared package still runs unchanged in both.
 *
 * Layout (big-endian, 128 bits):
 *
 *   unix_ts_ms (48) | ver = 0111 (4) | rand_a (12) | var = 10 (2) | rand_b (62)
 *
 * `rand_a` is used as a per-millisecond counter (RFC 9562 §6.2, method 1): the first id in a
 * millisecond seeds it randomly in its lower half, and every further id in the same millisecond
 * adds one, so ids minted back-to-back still sort strictly in the order they were minted. If the
 * counter runs out (more than ~2 000 ids in one millisecond) or the wall clock steps backwards,
 * the generator borrows the next millisecond rather than ever emitting an id that sorts before
 * one it already returned. `rand_b` is always fresh randomness.
 */

/** Sources a generator draws on — injectable so the ordering rules can be tested exactly. */
export interface EntityIdSources {
  /** Milliseconds since the Unix epoch. */
  now: () => number;
  /** Fills and returns `bytes` with cryptographically strong random values. */
  fillRandom: (bytes: Uint8Array) => Uint8Array;
}

const COUNTER_MAX = 0xfff;
/** Seeds stay in the lower half of the 12-bit counter, leaving headroom to count up. */
const COUNTER_SEED_MASK = 0x7ff;
const TWO_POW_32 = 0x1_0000_0000;

const HEX = Array.from({ length: 256 }, (_, byte) => byte.toString(16).padStart(2, '0'));

function defaultFillRandom(bytes: Uint8Array): Uint8Array {
  return globalThis.crypto.getRandomValues(bytes);
}

function seedCounter(bytes: Uint8Array): number {
  return ((bytes[6]! << 8) | bytes[7]!) & COUNTER_SEED_MASK;
}

/**
 * A UUID v7 generator with its own monotonic state. {@link newEntityId} is the one the product
 * uses; a separate generator is only needed by tests that pin the clock or the randomness.
 */
export function createEntityIdGenerator(sources: Partial<EntityIdSources> = {}): () => string {
  const now = sources.now ?? Date.now;
  const fillRandom = sources.fillRandom ?? defaultFillRandom;

  let lastMs = -1;
  let counter = 0;

  return function mintEntityId(): string {
    const bytes = fillRandom(new Uint8Array(16));

    let ms = Math.floor(now());
    if (ms > lastMs) {
      counter = seedCounter(bytes);
    } else {
      // Same millisecond, or the clock stepped back: stay on the last timestamp and count up.
      ms = lastMs;
      counter += 1;
      if (counter > COUNTER_MAX) {
        // Counter exhausted: borrow the next millisecond so order still holds.
        ms = lastMs + 1;
        counter = seedCounter(bytes);
      }
    }
    lastMs = ms;

    const high = Math.floor(ms / TWO_POW_32); // top 16 of the 48 timestamp bits
    const low = ms % TWO_POW_32;
    bytes[0] = (high >>> 8) & 0xff;
    bytes[1] = high & 0xff;
    bytes[2] = (low >>> 24) & 0xff;
    bytes[3] = (low >>> 16) & 0xff;
    bytes[4] = (low >>> 8) & 0xff;
    bytes[5] = low & 0xff;
    bytes[6] = 0x70 | (counter >>> 8); // version 7 in the high nibble, counter bits 11..8
    bytes[7] = counter & 0xff;
    bytes[8] = 0x80 | (bytes[8]! & 0x3f); // RFC 9562 variant: 10xx xxxx

    let out = '';
    for (let index = 0; index < 16; index += 1) {
      if (index === 4 || index === 6 || index === 8 || index === 10) out += '-';
      out += HEX[bytes[index]!]!;
    }
    return out;
  };
}

/**
 * A new entity id: a lowercase canonical UUID v7. Ids from successive calls in one process sort
 * lexicographically in the order they were minted.
 */
export const newEntityId: () => string = createEntityIdGenerator();
